var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import getContent, { init, postponeWarnings } from 'pp-react-l10n';
import { getProp, getLogger, getUserAgent, isMobile, postGql, b64DecodeUnicode } from '../common/utils';
import { getFnSyncPayload } from '../common/fn';
postponeWarnings();
const localizedContent = getContent();
const logger = getLogger();
export default function ({ context = '', onAuthEvent, options = {}, children, componentName = '' }) {
    const [authProviderContext, setAuthProviderContext] = useState();
    const [csrf, setCsrf] = useState('');
    const [isEEACountry, setIsEEACountry] = useState(false);
    const [csrfNonce, setCsrfNonce] = useState('');
    const [sessionID, setSessionID] = useState('');
    const [loadStartTime, setLoadStartTime] = useState(Date.now());
    const [challenges, setChallenges] = useState(null);
    const [nonce, setNonce] = useState('');
    const [challengeId, setChallengeId] = useState('');
    const [isPomaUser, setIsPomaUser] = useState(false);
    const [isAutoOtp, setIsAutoOtp] = useState(false);
    const [isOtpSent, setIsOtpSent] = useState(false);
    const [webAuthNChallengeObj, setWebAuthNChallengeObj] = useState(null);
    const [loginSchemes, setLoginSchemes] = useState(null);
    const [isContextReady, setIsContextReady] = useState(false);
    const [changeUser, setChangeUser] = useState(false);
    // componentName is used for fetching content json
    // Not setting up a componentName will allow development without provisioning dynamic content
    // It will also prevent logging the componentName hence it s best to provision dynamic content soon
    const [isContentLoaded, setIsContentLoaded] = useState(!componentName);
    const [maskedPhoneNumber, setMaskedPhoneNumber] = useState('');
    // `email` as well as `publicCredential` is currently in use
    // Once all the flavors support the grand lookup, we can get rid off email and setEmail
    const [email, setEmail] = useState('');
    const [publicCredential, setPublicCredential] = useState('');
    const [publicCredentialType, setPublicCredentialType] = useState('EMAIL');
    const [isCookiedUser, setIsCookiedUser] = useState(false);
    const [isWebLLSUser, setIsWebLLSUser] = useState(false);
    const [isWebAuthnUser, setIsWebAuthnUser] = useState(false);
    const [eligibleFeatures, setEligibleFeatures] = useState({});
    const [view, setView] = useState('');
    const [countryCode, setCountryCode] = useState('US');
    const [language, setLanguage] = useState('en');
    const [hasAuthViewMountedEffectRun, setHasAuthViewMountedEffectRun] = useState(false);
    // Wire up refs used in the unmount function
    const csrfRef = useRef(null);
    csrfRef.current = csrf;
    const sessionIDRef = useRef(null);
    sessionIDRef.current = sessionID;
    // log CPL for initial render (both cookied and uncookied)
    useEffect(() => {
        var _a, _b, _c, _d;
        if (!authProviderContext) {
            return;
        }
        const isCookied = ((_a = authProviderContext === null || authProviderContext === void 0 ? void 0 : authProviderContext.cookiedUser) === null || _a === void 0 ? void 0 : _a.email) || ((_b = authProviderContext === null || authProviderContext === void 0 ? void 0 : authProviderContext.cookiedUser) === null || _b === void 0 ? void 0 : _b.maskedPhoneNumber) ? true : false;
        // for cookied flow - view variable will be undefined initially until it's set by a child component
        // hence need to wait for when the value becomes defined
        // for uncookied flow - view variable will be always undefined so no need to wait for it
        if (!hasAuthViewMountedEffectRun && (!isCookied || view)) {
            const payload = {
                start: (options === null || options === void 0 ? void 0 : options.startTime) || Date.now(),
                t12: Date.now() - ((_d = (_c = window === null || window === void 0 ? void 0 : window.performance) === null || _c === void 0 ? void 0 : _c.timing) === null || _d === void 0 ? void 0 : _d.navigationStart),
                login_experience: 'Authprovider load',
            };
            if (options === null || options === void 0 ? void 0 : options.startTime) {
                payload.tt = Date.now() - (options === null || options === void 0 ? void 0 : options.startTime);
            }
            logger.track(`cpl_ap_loaded|${JSON.stringify(payload)}`);
            setHasAuthViewMountedEffectRun(true);
        }
    }, [authProviderContext, view, hasAuthViewMountedEffectRun]);
    useEffect(() => {
        var _a, _b, _c;
        try {
            setLoadStartTime(Date.now());
            // Parse the context obtained on the server via nodeIdentityToken module
            const parsedContext = JSON.parse(b64DecodeUnicode(context));
            setAuthProviderContext(parsedContext);
            setCsrfNonce(parsedContext.csrfNonce);
            const isCookiedUser = parsedContext.cookiedUser && (parsedContext.cookiedUser.email || parsedContext.cookiedUser.maskedPhoneNumber);
            if (parsedContext.eligibleFeatures) {
                let obj = {};
                parsedContext.eligibleFeatures.forEach((item) => {
                    obj[item] = 1;
                });
                setEligibleFeatures(obj);
            }
            if (!!isCookiedUser) {
                setIsWebLLSUser(parsedContext.cookiedUser.isWebLLSUser);
                setIsWebAuthnUser(parsedContext.cookiedUser.isWebAuthnUser);
                setEmail(parsedContext.cookiedUser.email || parsedContext.cookiedUser.maskedPhoneNumber);
                setPublicCredential(parsedContext.cookiedUser.email || parsedContext.cookiedUser.maskedPhoneNumber);
                setIsCookiedUser(true);
            }
            if (parsedContext.publicCredential) {
                setEmail(parsedContext.publicCredential);
                setPublicCredential(parsedContext.publicCredential);
            }
            // Allow overriding the public credential in case it s passed over from the client side
            if (options.publicCredential) {
                setEmail(options.publicCredential);
                setPublicCredential(options.publicCredential);
            }
            // Accumulate experiment and treatment IDs for logging
            const expIds = [];
            const trtIds = [];
            Object.keys(parsedContext.exp || {}).forEach((experiment) => {
                expIds.push(parsedContext.exp[experiment].expId);
                trtIds.push(parsedContext.exp[experiment].trtId);
                options[experiment] = options[parsedContext.exp[experiment]] || parsedContext.exp[experiment].value;
            });
            const isShowProfileEnabled = parsedContext.eligibleFeatures && parsedContext.eligibleFeatures.includes('showProfile');
            const isEmailCookied = ((_a = parsedContext === null || parsedContext === void 0 ? void 0 : parsedContext.cookiedUser) === null || _a === void 0 ? void 0 : _a.email) ? 'email' : '';
            const isPhoneCookied = ((_b = parsedContext === null || parsedContext === void 0 ? void 0 : parsedContext.cookiedUser) === null || _b === void 0 ? void 0 : _b.maskedPhoneNumber) ? 'phone' : '';
            // Wire up logger with the default context to be attached to each log
            logger.setFlowContext(Object.assign({ app_name: 'iwc', is_cookied: !!isCookiedUser ? 'Y' : 'N', is_pc_cookied_editable: isShowProfileEnabled && ((_c = parsedContext === null || parsedContext === void 0 ? void 0 : parsedContext.cookiedUser) === null || _c === void 0 ? void 0 : _c.email) ? 'Y' : 'N', pub_cred_type: isEmailCookied || isPhoneCookied, context_id: parsedContext.fnData && parsedContext.fnData.fnSessionId, device_type: isMobile() ? 'mobile' : 'desktop', 
                // Allow overriding flavor name for tracking common flavors
                // (eg. use XoUnifiedLogin as XoVipAuth [default] OR XoMemberAuth OR XoUnifiedLogin)
                flavor: options.trackingName || componentName, pgrp: 'auth', exp_ids: expIds.join(), trt_ids: trtIds.join(), locale_country: (options.locale && options.locale.countryCode) || (parsedContext.locale && parsedContext.locale.countryCode) }, (options.customTracking || {})));
            const fetchContent = () => __awaiter(this, void 0, void 0, function* () {
                let countryCode = (options.locale && options.locale.countryCode) ||
                    (parsedContext.locale && parsedContext.locale.countryCode) ||
                    'US';
                // In countryList.json the mapping for CN should be done to C2 until the country list is updated with missing CN data
                if (countryCode === 'CN') {
                    countryCode = 'C2';
                }
                const language = (options.locale && options.locale.language) ||
                    (parsedContext.locale && parsedContext.locale.language) ||
                    'en';
                // Update locale info in at the render props level so that the resolved values are available to all components
                setCountryCode(countryCode);
                setLanguage(language);
                const contentUrl = options.contentUrl || 'https://www.paypalobjects.com/iwc';
                try {
                    const contentRes = yield fetch(`${contentUrl}/${countryCode}/${language}/${componentName}.json`);
                    if (contentRes.ok) {
                        contentRes.json().then((content) => {
                            init({ content });
                            setIsContentLoaded(true);
                        });
                    }
                    else {
                        // Since the content download did not respond with OK,
                        // Make an assumption that the file did not exist for the locale
                        // and hence try downloading the content for US as a fallback
                        fetch(`${contentUrl}/US/en/${componentName}.json`)
                            .then((res) => res.json())
                            .then((content) => {
                            init({ content });
                            setIsContentLoaded(true);
                        });
                    }
                }
                catch (err) {
                    console.log(`Failed to load content file ${contentUrl}/${countryCode}/${language}/${componentName}.json`);
                    console.log(err);
                }
            });
            if (componentName) {
                fetchContent();
            }
            setIsContextReady(true);
            logger.track('ap_loaded');
            const isCookied = !!isCookiedUser ? true : false;
            onAuthEvent({
                event: 'authProviderLoaded',
                data: {
                    isCookiedUser: isCookied,
                    isWebLLSUser: isCookied && parsedContext.cookiedUser.isWebLLSUser ? true : false,
                },
            });
        }
        catch (err) {
            console.log(err, context);
        }
        return () => {
            logger.track('ap_unloaded');
            logger.flush({
                baseUri: options.baseUri,
                csrf: csrfRef.current,
                customFetch: options.customFetch,
                sessionID: sessionIDRef.current,
            });
        };
    }, []);
    const authEventHandler = ({ event, data }) => {
        onAuthEvent({
            event,
            data,
        });
    };
    const publicCredentialLookup = ({ publicCredential, publicCredentialType = 'EMAIL', maskedPhoneNumber }) => __awaiter(this, void 0, void 0, function* () {
        const challengeInfo = {
            autoSmsOtp: false,
        };
        Object.keys(authProviderContext.exp || {}).forEach((experiment) => {
            if (experiment === 'autoSmsOtp') {
                challengeInfo.autoSmsOtp = authProviderContext.exp && authProviderContext.exp[experiment].value;
            }
        });
        return yield postGql({
            data: {
                operationName: 'lookupOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {
                        fnId: authProviderContext.fnData.fnSessionId,
                        userAgent: getUserAgent(),
                        ctxId: authProviderContext.ctxId,
                        rData: getFnSyncPayload(),
                    },
                    credentials: {
                        credentialValue: publicCredential,
                        credentialType: publicCredentialType,
                        maskedPhoneNumber,
                    },
                    challengeInfo,
                },
            },
            baseUri: options.baseUri,
            timeout: options.timeout,
            env: authProviderContext.env,
        });
    });
    const updateAfterPublicCredentialLookup = ({ response = {}, publicCredential = '', publicCredentialType = 'EMAIL', }) => {
        setCsrf(response._csrf);
        setSessionID(response._sessionID);
        setPublicCredential(publicCredential);
        setPublicCredentialType(publicCredentialType);
        const getLookupResponse = getProp(response, 'data.login.lookup', {});
        const { preferredLogin, otpResponse, webAuthNResponse, isEEACountry } = getLookupResponse;
        setIsEEACountry(isEEACountry);
        if (!Array.isArray(preferredLogin)) {
            // User is not on file
            logger.track('no_options_found');
            return;
        }
        setLoginSchemes(preferredLogin);
        if (preferredLogin.length === 0) {
            // If a user is found, but only eligible for password login
            logger.track('empty_options_found');
            return;
        }
        if (preferredLogin.includes('OTP')) {
            // User is eligible for OTP
            const { nonce, isPomaUser, challenges } = otpResponse;
            setIsPomaUser(!!isPomaUser);
            setNonce(nonce);
            setMaskedPhoneNumber(challenges[0] && challenges[0].maskedPhoneNumber);
            setChallenges(challenges);
        }
        if (preferredLogin.includes('WEBAUTHN')) {
            // User is eligible for WEBAUTHN
            const { challenge, allowCredentials, replyingPartyId } = webAuthNResponse;
            const regex = /(.+):\d+/gm;
            const match = regex.exec(replyingPartyId);
            const rpId = match ? match[1] : replyingPartyId;
            setWebAuthNChallengeObj({ challenge, allowCredentials, rpId });
        }
    };
    const lookup = ({ email }) => __awaiter(this, void 0, void 0, function* () {
        const challengeInfo = {
            autoSmsOtp: false,
        };
        Object.keys(authProviderContext.exp || {}).forEach((experiment) => {
            if (experiment === 'autoSmsOtp') {
                challengeInfo.autoSmsOtp = authProviderContext.exp && authProviderContext.exp[experiment].value;
            }
        });
        return yield postGql({
            data: {
                operationName: 'getOtpChallengeOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {
                        fnId: authProviderContext.fnData.fnSessionId,
                        userAgent: getUserAgent(),
                        ctxId: authProviderContext.ctxId,
                        rData: getFnSyncPayload(),
                    },
                    credentials: {
                        credentialValue: email,
                        credentialType: 'EMAIL',
                    },
                    challengeInfo,
                },
            },
            baseUri: options.baseUri,
            timeout: options.timeout,
            env: authProviderContext.env,
            customFetch: options.customFetch,
        });
    });
    const updateAfterLookup = ({ response = {}, email = '' }) => {
        setCsrf(response._csrf);
        setSessionID(response._sessionID);
        setEmail(email);
        const getOtpChallengeRes = getProp(response, 'data.otp.getOtpChallenge', {});
        setIsPomaUser(!!getOtpChallengeRes.isPomaUser);
        const challenges = getOtpChallengeRes.challenges;
        // If a user is not on file, challenges will be null
        if (!Array.isArray(challenges)) {
            logger.track('no_options_found');
            return;
        }
        if (challenges.length > 0) {
            setNonce(getOtpChallengeRes.nonce);
            setMaskedPhoneNumber(challenges[0] && challenges[0].maskedPhoneNumber);
            setChallenges(challenges);
        }
        else {
            // If a user is found, but is not eligble for the flow,
            // then the challenges array will be empty
            logger.track('empty_options_found');
        }
    };
    const forgetUser = ({ updatedCsrf = '', updatedSessionID = '' }) => {
        setIsCookiedUser(false);
        setEmail('');
        setPublicCredential('');
        setIsOtpSent(false);
        setChallenges(null);
        setChangeUser(true);
        logger.track('user_switched');
        postGql({
            data: {
                operationName: 'forgetUserOperation',
                query: '',
                variables: {},
            },
            csrf: updatedCsrf ? updatedCsrf : csrf,
            sessionID: updatedSessionID ? updatedSessionID : sessionID,
            baseUri: options.baseUri,
        });
    };
    const sendOtpChallenge = ({ selectedChallengeId, selectedMaskedPhoneNumber }) => __awaiter(this, void 0, void 0, function* () {
        setChallengeId(selectedChallengeId);
        if (selectedMaskedPhoneNumber) {
            setMaskedPhoneNumber(selectedMaskedPhoneNumber);
        }
        const response = yield postGql({
            data: {
                operationName: 'sendOtpChallengeOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {
                        fnId: authProviderContext.fnData.fnSessionId,
                        userAgent: getUserAgent(),
                        ctxId: authProviderContext.ctxId,
                        rData: getFnSyncPayload(),
                    },
                    notificationInfo: {
                        challengeId: selectedChallengeId,
                        nonce,
                    },
                },
            },
            csrf,
            sessionID,
            baseUri: options.baseUri,
            customFetch: options.customFetch,
        });
        return response;
    });
    const updateAfterOtpSent = ({ response }) => {
        setIsOtpSent(true);
        setNonce(getProp(response, 'data.otp.sendOtpNotification.nonce', ''));
    };
    const verifyPrivateCredential = ({ email, password, targetApplication = null, maskedPhoneNumber }) => __awaiter(this, void 0, void 0, function* () {
        return yield postGql({
            data: {
                operationName: 'verifyPrivateCredentialOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {
                        fnId: authProviderContext.fnData.fnSessionId,
                        userAgent: getUserAgent(),
                        ctxId: authProviderContext.ctxId,
                        rData: getFnSyncPayload(),
                    },
                    credentials: {
                        publicCredential: {
                            credentialValue: email,
                            credentialType: publicCredentialType,
                            maskedPhoneNumber,
                        },
                        credentialValue: password,
                        credentialType: 'PASSWORD',
                    },
                    targetApplication,
                },
            },
            csrf,
            sessionID,
            baseUri: options.baseUri,
        });
    });
    const verifyOtp = ({ securityCode }) => __awaiter(this, void 0, void 0, function* () {
        const response = yield postGql({
            data: {
                operationName: 'verifyOtpOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {
                        fnId: authProviderContext.fnData.fnSessionId,
                        ctxId: authProviderContext.ctxId,
                        rData: getFnSyncPayload(),
                        userAgent: getUserAgent(),
                    },
                    verificationInfo: {
                        nonce,
                        securityCode,
                    },
                    credentials: {
                        credentialValue: publicCredential,
                        credentialType: publicCredentialType,
                    },
                },
            },
            csrf,
            sessionID,
            baseUri: options.baseUri,
            customFetch: options.customFetch,
        });
        return response;
    });
    const performWebLLSLogin = () => __awaiter(this, void 0, void 0, function* () {
        const response = yield postGql({
            data: {
                operationName: 'performWebLLSLoginOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {
                        fnId: authProviderContext.fnData.fnSessionId,
                        ctxId: authProviderContext.ctxId,
                        rData: getFnSyncPayload(),
                        userAgent: getUserAgent(),
                    },
                    credentials: {
                        credentialValue: authProviderContext.cookiedUser && authProviderContext.cookiedUser.email,
                        credentialType: 'EMAIL',
                    },
                },
            },
            baseUri: options.baseUri,
        });
        return response;
    });
    const setLoginPreferenceInCookie = ({ csrf = '', sessionID = '', preference }) => __awaiter(this, void 0, void 0, function* () {
        postGql({
            data: {
                operationName: 'setLoginPreferenceOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {},
                    loginPreference: preference,
                },
            },
            csrf,
            sessionID,
            baseUri: options.baseUri,
        });
    });
    const getCreateChallenge = () => __awaiter(this, void 0, void 0, function* () {
        const response = yield postGql({
            data: {
                operationName: 'getCreateChallengeOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {
                        fnId: authProviderContext.fnData.fnSessionId,
                        ctxId: authProviderContext.ctxId,
                        rData: getFnSyncPayload(),
                        userAgent: getUserAgent(),
                    },
                    credentials: {
                        credentialValue: publicCredential,
                        credentialType: publicCredentialType,
                    },
                },
            },
            baseUri: options.baseUri,
        });
        return response;
    });
    const optinOperation = (publicKey) => __awaiter(this, void 0, void 0, function* () {
        const response = yield postGql({
            data: {
                operationName: 'optinOperation',
                query: '',
                log: logger.extract(),
                csrfNonce,
                variables: {
                    clientInfo: {
                        fnId: authProviderContext.fnData.fnSessionId,
                        ctxId: authProviderContext.ctxId,
                        rData: getFnSyncPayload(),
                        userAgent: getUserAgent(),
                    },
                    credentials: {
                        credentialValue: publicKey,
                        credentialType: 'AUTHENTICATOR_RESPONSE',
                        publicCredential: {
                            credentialValue: publicCredential,
                            credentialType: publicCredentialType,
                        },
                    },
                },
            },
            baseUri: options.baseUri,
        });
        return response;
    });
    return children({
        authEventHandler,
        authProviderContext,
        challengeId,
        challenges,
        countryCode,
        csrf,
        csrfNonce,
        email,
        forgetUser,
        isAutoOtp,
        isContentLoaded,
        isCookiedUser,
        isOtpSent,
        isPomaUser,
        isWebAuthnUser,
        isWebLLSUser,
        loadStartTime,
        language,
        localizedContent,
        logger,
        loginSchemes,
        lookup,
        maskedPhoneNumber,
        options,
        performWebLLSLogin,
        publicCredential,
        publicCredentialLookup,
        publicCredentialType,
        sendOtpChallenge,
        setChallengeId,
        setCountryCode,
        setCsrf,
        setEmail,
        setIsAutoOtp,
        setIsOtpSent,
        setIsWebAuthnUser,
        setIsWebLLSUser,
        setLoginPreferenceInCookie,
        setPublicCredential,
        setView,
        updateAfterLookup,
        updateAfterOtpSent,
        updateAfterPublicCredentialLookup,
        getCreateChallenge,
        optinOperation,
        isEEACountry,
        verifyOtp,
        verifyPrivateCredential,
        view,
        webAuthNChallengeObj,
        eligibleFeatures,
        isContextReady,
        changeUser,
    });
}
